import React, { useState, useEffect } from 'react'
import { Users, Loader2, Link2 } from 'lucide-react'
import { Drawer, ImageUpload } from '../ui'
import { useToast } from '../../contexts/ToastContext'
import usePartnerStore from '../../stores/partnerStore'
import usePartnerCategoryStore from '../../stores/partnerCategoryStore'
import usePartnerTierStore from '../../stores/partnerTierStore'
import useGlobalCustomerStore from '../../stores/globalCustomerStore'
import { fileToBase64, getContentType } from '../../utils/imageUtils'
import { isValidUrl } from '../../utils/validation'
import { Partner } from '../../types/partner'

interface AddPartnerDrawerProps {
  isOpen: boolean
  onClose: () => void
  /** Pre-fills the name, e.g. from a brand detected on a broadcast. */
  initialName?: string
  /**
   * Detected spellings to save as aliases, so the new sponsor matches them
   * even if the name above is edited. Shown read-only in the form.
   */
  brandAliases?: string[]
  /** Called after a successful create, before the drawer closes. */
  onCreated?: (partner: Partner) => void | Promise<void>
}

const AddPartnerDrawer: React.FC<AddPartnerDrawerProps> = ({
  isOpen, onClose, initialName, brandAliases = [], onCreated
}) => {
  const { showSuccess, showError } = useToast()
  const { createPartner, loadPartners } = usePartnerStore()
  const { categories, loadCategories } = usePartnerCategoryStore()
  const { tiers, loadTiers } = usePartnerTierStore()
  const { selectedCustomer } = useGlobalCustomerStore()

  const [name, setName] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [tierId, setTierId] = useState('')
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState<'Active' | 'Inactive' | 'Pending' | 'Suspended'>('Active')
  const [isCreating, setIsCreating] = useState(false)

  // Logo upload state
  const [logoBase64, setLogoBase64] = useState<string | null>(null)
  const [logoFilename, setLogoFilename] = useState<string | null>(null)
  const [logoContentType, setLogoContentType] = useState<string | null>(null)
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null)
  const [isUploadingLogo, setIsUploadingLogo] = useState(false)

  // Pre-fill on open; the user can still edit it.
  useEffect(() => {
    if (isOpen && initialName) setName(initialName)
  }, [isOpen, initialName])

  // Load categories and tiers when drawer opens
  useEffect(() => {
    if (isOpen) {
      loadCategories(true) // Only load active categories
      loadTiers(true) // Only load active tiers
    }
  }, [isOpen, loadCategories, loadTiers])

  const resetForm = () => {
    setName('')
    setCategoryId('')
    setTierId('')
    setWebsiteUrl('')
    setDescription('')
    setStatus('Active')
    setLogoBase64(null)
    setLogoFilename(null)
    setLogoContentType(null)
    setLogoPreviewUrl(null)
  }

  // Handle logo upload - converts file to base64 for API
  const handleLogoUpload = async (file: File) => {
    setIsUploadingLogo(true)
    try {
      const base64 = await fileToBase64(file)
      const contentType = getContentType(file)

      setLogoBase64(base64)
      setLogoFilename(file.name)
      setLogoContentType(contentType)
      // Create preview URL from base64 (must be set before this function returns)
      setLogoPreviewUrl(`data:${contentType};base64,${base64}`)
    } catch (error) {
      showError('Upload Failed', 'Failed to process logo image')
    } finally {
      setIsUploadingLogo(false)
    }
  }

  // Handle logo removal
  const handleLogoRemove = async () => {
    setLogoBase64(null)
    setLogoFilename(null)
    setLogoContentType(null)
    setLogoPreviewUrl(null)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedCustomer?.id) {
      showError('Error', 'Please select a customer first')
      return
    }

    if (!name.trim()) {
      showError('Validation Error', 'Sponsor name is required')
      return
    }

    if (name.length > 255) {
      showError('Validation Error', 'Sponsor name must be 255 characters or less')
      return
    }

    if (!categoryId) {
      showError('Validation Error', 'Sponsor category is required')
      return
    }

    if (websiteUrl && !isValidUrl(websiteUrl)) {
      showError('Validation Error', 'Please enter a valid website URL')
      return
    }

    setIsCreating(true)

    try {
      const newPartner = await createPartner(
        String(selectedCustomer.id),
        {
          name: name.trim(),
          category_id: categoryId,
          tier_id: tierId || null,
          website_url: websiteUrl.trim() || undefined,
          description: description.trim() || undefined,
          status,
          ...(brandAliases.length > 0 && { brand_aliases: brandAliases }),
          // Include logo if uploaded
          ...(logoBase64 && logoFilename && logoContentType && {
            logo_base64: logoBase64,
            logo_filename: logoFilename,
            logo_content_type: logoContentType
          })
        }
      )

      // A caller with onCreated says what happened in its own words.
      if (!onCreated) {
        showSuccess('Sponsor Created', `"${newPartner.name}" has been created successfully`)
      }

      // Reload partners list to get the full partner data with categories and tiers
      await loadPartners(String(selectedCustomer.id))
      await onCreated?.(newPartner)

      handleClose()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      showError(
        'Failed to create sponsor',
        errorMessage.includes('already exists')
          ? 'A sponsor with this name already exists for this customer'
          : 'Please try again later'
      )
    } finally {
      setIsCreating(false)
    }
  }

  // Get active categories and tiers
  const activeCategories = categories.filter((c) => c.is_active)
  const activeTiers = tiers.filter((t) => t.is_active)

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Sponsor"
      description="Create a new sponsor"
      icon={<Users className="h-5 w-5" />}
      width="md"
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Partner Name */}
        <div>
          <label htmlFor="partner-name" className="block text-xs font-medium text-gray-700 mb-1">
            Sponsor Name <span className="text-red-500">*</span>
          </label>
          <input
            id="partner-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Acme Corporation"
            maxLength={255}
            disabled={isCreating}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            autoFocus
          />
        </div>

        {/* Shown only when creating from a detected brand: says which
            spellings will be linked, so editing the name above is safe. */}
        {brandAliases.length > 0 && (
          <div className="rounded-lg border border-blue-100 bg-blue-50/60 px-2.5 py-2">
            <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-blue-800">
              <Link2 className="h-3 w-3" />Detected as
            </p>
            <div className="flex flex-wrap gap-1 mt-1">
              {brandAliases.map((a) => (
                <span key={a} className="text-[11px] font-mono text-blue-900 bg-white border border-blue-100 rounded px-1.5 py-0.5">{a}</span>
              ))}
            </div>
            <p className="text-[11px] text-blue-700 mt-1.5 leading-snug">
              These spellings are linked to the new sponsor, so its broadcast exposure is
              credited even if you change the name.
            </p>
          </div>
        )}

        {/* Logo Upload */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Logo
          </label>
          <ImageUpload
            currentImageUrl={logoPreviewUrl}
            onUpload={handleLogoUpload}
            onRemove={handleLogoRemove}
            accept="image/png,image/jpeg,image/webp"
            maxSizeMB={5}
            disabled={isCreating}
            isUploading={isUploadingLogo}
          />
          <p className="text-xs text-gray-500 mt-1">
            PNG, JPEG, or WebP up to 5MB. Any aspect ratio supported.
          </p>
        </div>

        {/* Category and Tier Row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="category" className="block text-xs font-medium text-gray-700 mb-1">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              id="category"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              disabled={isCreating}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              <option value="">Select category</option>
              {activeCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="tier" className="block text-xs font-medium text-gray-700 mb-1">
              Tier
            </label>
            <select
              id="tier"
              value={tierId}
              onChange={(e) => setTierId(e.target.value)}
              disabled={isCreating}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              <option value="">No tier</option>
              {activeTiers.map((tier) => (
                <option key={tier.id} value={tier.id}>
                  {tier.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Website URL and Status Row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="website-url" className="block text-xs font-medium text-gray-700 mb-1">
              Website URL
            </label>
            <input
              id="website-url"
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://example.com"
              disabled={isCreating}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            />
          </div>
          <div>
            <label htmlFor="status" className="block text-xs font-medium text-gray-700 mb-1">
              Status <span className="text-red-500">*</span>
            </label>
            <select
              id="status"
              value={status}
              onChange={(e) =>
                setStatus(e.target.value as 'Active' | 'Inactive' | 'Pending' | 'Suspended')
              }
              disabled={isCreating}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Description */}
        <div>
          <label htmlFor="partner-description" className="block text-xs font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            id="partner-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter a description (optional)"
            rows={2}
            disabled={isCreating}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed resize-none"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={isCreating}
            className="flex-1 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isCreating || !name.trim() || !categoryId}
            className="flex-1 inline-flex items-center justify-center px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreating ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Users className="h-3.5 w-3.5 mr-1.5" />
                Create Sponsor
              </>
            )}
          </button>
        </div>
      </form>
    </Drawer>
  )
}

export default AddPartnerDrawer
